import type { FC } from 'react';
import { useEffect } from 'react';

import { LGASummary } from '../../../app/electoral-divisions/lga/lga-summary';
import { PUSummary } from '../../../app/electoral-divisions/polling-unit/pu-summary';
import { StateSummary } from '../../../app/electoral-divisions/state/state-summary';
import { WardSummary } from '../../../app/electoral-divisions/ward/ward-summary';
import { useGlobalContext } from '../../../context/global';
import { useRightPanelContext } from '../../../context/right-section';
import {
	useGetLGAByCodeQuery,
	useGetPollingUnitByCodeMutation,
	useGetStateByCodeMutation,
	useGetWardByCodeMutation,
} from '../../../store/api/core';
import ErrorBoundary from '../../error-boundary';
import type { ElectoralCodeLevel } from '../helper';

interface CodePeekBodyProps {
	level: ElectoralCodeLevel;
	code: string;
}

const PeekErrorFallback: FC<{ level: ElectoralCodeLevel; code: string }> = ({ level, code }) => (
	<div className='code-peek__empty'>
		<p className='code-peek__empty-title'>Unable to load preview</p>
		<p className='code-peek__empty-detail'>
			Could not render the {level} details for <strong>{code}</strong>. Try opening the full details panel instead.
		</p>
	</div>
);

const LoadingState: FC = () => (
	<div className='code-peek__loading'>
		<div className='code-peek__skeleton-line' />
		<div className='code-peek__skeleton-line' />
		<div className='code-peek__skeleton-line code-peek__skeleton-line--short' />
		<div className='code-peek__skeleton-line' />
	</div>
);

const EmptyState: FC<{ level: ElectoralCodeLevel; code: string }> = ({ level, code }) => (
	<div className='code-peek__empty'>
		<p className='code-peek__empty-title'>Not found</p>
		<p className='code-peek__empty-detail'>
			No {level} record exists for code <strong>{code}</strong> in this election cycle.
		</p>
	</div>
);

const StatePeekBody: FC<{ code: string }> = ({ code }) => {
	const { global } = useGlobalContext();
	const rightPanel = useRightPanelContext();
	const [trigger, result] = useGetStateByCodeMutation({ fixedCacheKey: `peek-state-${code}` });
	useEffect(() => {
		void trigger({ code });
	}, [trigger, code]);
	if (result.isLoading || result.isUninitialized) return <LoadingState />;
	if (!result.data?.isSuccessful || !result.data?.data)
		return (
			<EmptyState
				level='state'
				code={code}
			/>
		);
	return (
		<StateSummary
			stateData={result.data as never}
			rightPanel={rightPanel}
			global={global}
		/>
	);
};

const LGAPeekBody: FC<{ code: string }> = ({ code }) => {
	const rightPanel = useRightPanelContext();
	const result = useGetLGAByCodeQuery({ code });
	if (result.isLoading || result.isFetching) return <LoadingState />;
	const raw = result.data?.data;
	const item = Array.isArray(raw) ? raw[0] : raw;
	if (!result.data?.isSuccessful || !item)
		return (
			<EmptyState
				level='lga'
				code={code}
			/>
		);
	const adapted = { ...result.data, data: item };
	return (
		<LGASummary
			lgaData={adapted as never}
			rightPanel={rightPanel}
		/>
	);
};

const WardPeekBody: FC<{ code: string }> = ({ code }) => {
	const { global } = useGlobalContext();
	const rightPanel = useRightPanelContext();
	const [trigger, result] = useGetWardByCodeMutation({ fixedCacheKey: `peek-ward-${code}` });
	useEffect(() => {
		void trigger({ wardCode: code });
	}, [trigger, code]);
	if (result.isLoading || result.isUninitialized) return <LoadingState />;
	if (!result.data?.isSuccessful || !result.data?.data)
		return (
			<EmptyState
				level='ward'
				code={code}
			/>
		);
	return (
		<WardSummary
			wardData={result.data as never}
			rightPanel={rightPanel}
			global={global}
		/>
	);
};

const PUPeekBody: FC<{ code: string }> = ({ code }) => {
	const { global } = useGlobalContext();
	const rightPanel = useRightPanelContext();
	const [trigger, result] = useGetPollingUnitByCodeMutation({ fixedCacheKey: `peek-pu-${code}` });
	useEffect(() => {
		void trigger({ poolingUnitCode: code });
	}, [trigger, code]);
	if (result.isLoading || result.isUninitialized) return <LoadingState />;
	if (!result.data?.isSuccessful || !result.data?.data)
		return (
			<EmptyState
				level='pollingUnit'
				code={code}
			/>
		);
	return (
		<PUSummary
			puData={result.data as never}
			rightPanel={rightPanel}
			global={global}
		/>
	);
};

const CodePeekBodyInner: FC<CodePeekBodyProps> = ({ level, code }) => {
	switch (level) {
		case 'state':
			return <StatePeekBody code={code} />;
		case 'lga':
			return <LGAPeekBody code={code} />;
		case 'ward':
			return <WardPeekBody code={code} />;
		case 'pollingUnit':
			return <PUPeekBody code={code} />;
		default:
			return null;
	}
};

export const CodePeekBody: FC<CodePeekBodyProps> = ({ level, code }) => (
	<ErrorBoundary
		resetKey={`${level}:${code}`}
		fallBackUI={
			<PeekErrorFallback
				level={level}
				code={code}
			/>
		}
	>
		<CodePeekBodyInner
			level={level}
			code={code}
		/>
	</ErrorBoundary>
);
